import {
  Alert,
  Button,
  CollapsibleCard,
  DateInput,
  Row,
  TextInput,
  Tooltip,
  IconButton,
} from "@narmi/design_system";
import React, { useCallback, useContext, useState } from "react";
import { useLocalization } from "@fluent/react";
import { DateTime } from "luxon";
import { downloadImage } from "../../../common/file_utils";
import AmountTextInput from "../../form/AmountTextInput";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import CheckDetailContext from "./context";
import CheckDetailProvider from "./CheckDetailProvider";
import styles from "./CheckManager.module.scss";
import type { CheckManagerHeaderProps, TransactionCheckProps } from "./types";

const CheckManagerTitle = ({
  title,
  isCollapsed = false,
  onClick,
}: CheckManagerHeaderProps) => (
  <div className={styles.title} onClick={onClick}>
    <Row justifyContent="space-between" alignItems="center">
      <Row.Item>{title}</Row.Item>
      <Row.Item shrink>
        <IconButton
          name={isCollapsed ? "chevron-down" : "chevron-up"}
          textSize={"m"}
        />
      </Row.Item>
    </Row>
  </div>
);

const TransactionCheckContent = ({
  label,
  front,
  back,
  isCheck,
}: Pick<TransactionCheckProps, "label" | "front" | "back" | "isCheck">) => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const isCheckImageOcrEnabled = Boolean(features.check_image_ocr);
  const [isDisclaimerDismissed, setIsDisclaimerDismissed] = useState(false);
  // Track which field is currently focused so the NDS character counter
  // (which renders whenever `maxLength` is set) only shows on the focused
  // input. `maxLength` is passed only to the focused field.
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const MAX_TEXT_LENGTH = 128;

  const {
    isCollapsed,
    isEditing,
    checkDetailUuid,
    displayPayFrom,
    displayPayTo,
    displayMemo,
    displayAmount,
    displayCheckDate,
    displayCheckNumber,
    editPayFrom,
    editPayTo,
    editMemo,
    editAmount,
    editCheckDate,
    editCheckNumber,
    setEditPayFrom,
    setEditPayTo,
    setEditMemo,
    setEditAmount,
    setEditCheckDate,
    setEditCheckNumber,
    toggleCollapsed,
    handleEdit,
    handleCancel,
    handleSave,
    isSaving,
  } = useContext(CheckDetailContext);

  const renderImageWithOverlay = useCallback(
    (imageData: string, altText: string, filename: string) => (
      <div
        className={styles.imageContainer}
        onClick={() => downloadImage(imageData, filename)}
      >
        <img
          src={`data:;base64,${imageData}`}
          alt={altText}
          className={styles.image}
        />
        <div className={styles.imageOverlay}>
          <IconButton
            name="download"
            testId="download-check-image-button"
            textSize="l"
            className={styles.downloadIcon}
          />
        </div>
      </div>
    ),
    [downloadImage]
  );

  const renderTitle = useCallback(
    () => (
      <CheckManagerTitle
        title={label}
        onClick={toggleCollapsed}
        isCollapsed={isCollapsed}
      />
    ),
    [label, toggleCollapsed, isCollapsed]
  );

  const frontUnavailable = l10n.getString(
    "transaction-detail-check-front-unavailable",
    {},
    "Front image unavailable"
  );
  const backUnavailable = l10n.getString(
    "transaction-detail-check-back-unavailable",
    {},
    "Back image unavailable"
  );

  const downloadImageTip = l10n.getString(
    "transaction-detail-check-download-image",
    {},
    "Download this image to see in high res"
  );

  const emptyValue = "\u2014";

  return (
    <div className="alignChild--center--center ">
      <CollapsibleCard
        trigger="caret-start"
        isOpen={!isCollapsed}
        onClose={toggleCollapsed}
        onOpen={toggleCollapsed}
        renderTitle={renderTitle}
      >
        <React.Fragment key=".0">
          <Row>
            <Row.Item>
              {front ? (
                <Tooltip text={downloadImageTip}>
                  {renderImageWithOverlay(
                    front,
                    l10n.getString(
                      "transaction-detail-check-front-alt",
                      null,
                      "Front of check"
                    ),
                    `${label}-Front.jpg`
                  )}
                </Tooltip>
              ) : (
                <div className={styles.noImageTextContainer}>
                  {frontUnavailable}
                </div>
              )}
            </Row.Item>
            <Row.Item>
              {back ? (
                <Tooltip text={downloadImageTip}>
                  {renderImageWithOverlay(
                    back,
                    l10n.getString(
                      "transaction-detail-check-back-alt",
                      null,
                      "Back of check"
                    ),
                    `${label}-Back.jpg`
                  )}
                </Tooltip>
              ) : (
                <div className={styles.noImageTextContainer}>
                  {backUnavailable}
                </div>
              )}
            </Row.Item>
          </Row>
          {isCheckImageOcrEnabled && isCheck !== false && checkDetailUuid && (
            <div className={styles.checkDetails}>
              <div>
                <Row alignItems="center">
                  <Row.Item>
                    <span className={styles.checkDetailsHeaderText}>
                      {l10n.getString(
                        "transaction-detail-check-details-header",
                        {},
                        "Check Details"
                      )}
                    </span>
                  </Row.Item>
                  {!isEditing && (
                    <Row.Item shrink>
                      <Button
                        kind="plain"
                        size="xs"
                        startIcon="edit-2"
                        onClick={handleEdit}
                        label={l10n.getString(
                          "transaction-detail-check-edit",
                          {},
                          "Edit"
                        )}
                      />
                    </Row.Item>
                  )}
                </Row>
              </div>
              <div className={styles.checkDetailsDisclaimerWrapper}>
                <Alert
                  isActive={!isDisclaimerDismissed}
                  kind="info"
                  paddingSize="s"
                  isDismissable
                  onUserDismiss={() => setIsDisclaimerDismissed(true)}
                >
                  <span className={styles.checkDetailsDisclaimerText}>
                    {l10n.getString(
                      "transaction-detail-check-ai-disclaimer",
                      {},
                      "Check information was initially read using AI. Edits made to check details only affect how the transaction is displayed, not the transaction record."
                    )}
                  </span>
                </Alert>
              </div>
              {isEditing ? (
                <>
                  <div className={styles.checkDetailRows}>
                    <div className={styles.checkDetailRowWrapper}>
                      <Row alignItems="center">
                        <Row.Item>
                          <span className={styles.checkDetailLabel}>
                            {l10n.getString(
                              "transaction-detail-check-payee-label",
                              {},
                              "Payee"
                            )}
                          </span>
                        </Row.Item>
                        <Row.Item shrink>
                          <div className={styles.checkDetailEditInput}>
                            <TextInput
                              field="pay_to"
                              value={editPayTo}
                              maxLength={
                                focusedField === "pay_to"
                                  ? MAX_TEXT_LENGTH
                                  : undefined
                              }
                              onFocus={() => setFocusedField("pay_to")}
                              onBlur={() => setFocusedField(null)}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setEditPayTo(
                                  e.target.value.slice(0, MAX_TEXT_LENGTH)
                                )
                              }
                            />
                          </div>
                        </Row.Item>
                      </Row>
                    </div>
                    <div className={styles.checkDetailRowWrapper}>
                      <Row alignItems="center">
                        <Row.Item>
                          <span className={styles.checkDetailLabel}>
                            {l10n.getString(
                              "transaction-detail-check-payer-label",
                              {},
                              "Payer"
                            )}
                          </span>
                        </Row.Item>
                        <Row.Item shrink>
                          <div className={styles.checkDetailEditInput}>
                            <TextInput
                              field="pay_from"
                              value={editPayFrom}
                              maxLength={
                                focusedField === "pay_from"
                                  ? MAX_TEXT_LENGTH
                                  : undefined
                              }
                              onFocus={() => setFocusedField("pay_from")}
                              onBlur={() => setFocusedField(null)}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setEditPayFrom(
                                  e.target.value.slice(0, MAX_TEXT_LENGTH)
                                )
                              }
                            />
                          </div>
                        </Row.Item>
                      </Row>
                    </div>
                    <div className={styles.checkDetailRowWrapper}>
                      <Row alignItems="center">
                        <Row.Item>
                          <span className={styles.checkDetailLabel}>
                            {l10n.getString(
                              "transaction-detail-check-amount-label",
                              {},
                              "Amount"
                            )}
                          </span>
                        </Row.Item>
                        <Row.Item shrink>
                          <div className={styles.checkDetailEditInput}>
                            <AmountTextInput
                              field="amount"
                              label=""
                              value={editAmount}
                              onChange={(value: string) => setEditAmount(value)}
                            />
                          </div>
                        </Row.Item>
                      </Row>
                    </div>
                    <div className={styles.checkDetailRowWrapper}>
                      <Row alignItems="center">
                        <Row.Item>
                          <span className={styles.checkDetailLabel}>
                            {l10n.getString(
                              "transaction-detail-check-date-label",
                              {},
                              "Check date"
                            )}
                          </span>
                        </Row.Item>
                        <Row.Item shrink>
                          <div className={styles.checkDetailEditInput}>
                            <DateInput
                              value={editCheckDate}
                              altInput={true}
                              altFormat={"m/d/Y"}
                              disableDates={[
                                (date: Date) =>
                                  date >=
                                  DateTime.now()
                                    .plus({ days: 1 })
                                    .startOf("day")
                                    .toJSDate(),
                              ]}
                              onChange={(value: string) =>
                                setEditCheckDate(value)
                              }
                            />
                          </div>
                        </Row.Item>
                      </Row>
                    </div>
                    <div className={styles.checkDetailRowWrapper}>
                      <Row alignItems="center">
                        <Row.Item>
                          <span className={styles.checkDetailLabel}>
                            {l10n.getString(
                              "transaction-detail-check-memo-label",
                              {},
                              "Memo"
                            )}
                          </span>
                        </Row.Item>
                        <Row.Item shrink>
                          <div className={styles.checkDetailEditInput}>
                            <TextInput
                              field="memo"
                              value={editMemo}
                              maxLength={
                                focusedField === "memo"
                                  ? MAX_TEXT_LENGTH
                                  : undefined
                              }
                              onFocus={() => setFocusedField("memo")}
                              onBlur={() => setFocusedField(null)}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setEditMemo(
                                  e.target.value.slice(0, MAX_TEXT_LENGTH)
                                )
                              }
                            />
                          </div>
                        </Row.Item>
                      </Row>
                    </div>
                    <div className={styles.checkDetailRowWrapper}>
                      <Row alignItems="center">
                        <Row.Item>
                          <span className={styles.checkDetailLabel}>
                            {l10n.getString(
                              "transaction-detail-check-number-label",
                              {},
                              "Check number"
                            )}
                          </span>
                        </Row.Item>
                        <Row.Item shrink>
                          <div className={styles.checkDetailEditInput}>
                            <TextInput
                              field="check_number"
                              value={editCheckNumber}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setEditCheckNumber(
                                  e.target.value.replace(/\D/g, "")
                                )
                              }
                            />
                          </div>
                        </Row.Item>
                      </Row>
                    </div>
                  </div>
                  <div className={styles.separator} />
                  <div>
                    <Row alignItems="center" justifyContent="end" gapSize="xs">
                      <Row.Item shrink>
                        <div className={styles.checkDetailsEditFooterButton}>
                          <Button
                            kind="plain"
                            size="xs"
                            onClick={handleCancel}
                            label={l10n.getString(
                              "transaction-detail-check-cancel",
                              {},
                              "Cancel"
                            )}
                          />
                        </div>
                      </Row.Item>
                      <Row.Item shrink>
                        <div className={styles.checkDetailsEditFooterButton}>
                          <Button
                            size="xs"
                            onClick={handleSave}
                            disabled={isSaving}
                            label={l10n.getString(
                              "transaction-detail-check-save",
                              {},
                              "Save changes"
                            )}
                          />
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                </>
              ) : (
                <div className={styles.checkDetailRows}>
                  <div className={styles.checkDetailRowWrapper}>
                    <Row alignItems="center">
                      <Row.Item shrink>
                        <span className={styles.checkDetailLabel}>
                          {l10n.getString(
                            "transaction-detail-check-payee-label",
                            {},
                            "Payee"
                          )}
                        </span>
                      </Row.Item>
                      <Row.Item>
                        <div className={styles.checkDetailValue}>
                          {displayPayTo || emptyValue}
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                  <div className={styles.checkDetailRowWrapper}>
                    <Row alignItems="center">
                      <Row.Item shrink>
                        <span className={styles.checkDetailLabel}>
                          {l10n.getString(
                            "transaction-detail-check-payer-label",
                            {},
                            "Payer"
                          )}
                        </span>
                      </Row.Item>
                      <Row.Item>
                        <div className={styles.checkDetailValue}>
                          {displayPayFrom || emptyValue}
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                  <div className={styles.checkDetailRowWrapper}>
                    <Row alignItems="center">
                      <Row.Item shrink>
                        <span className={styles.checkDetailLabel}>
                          {l10n.getString(
                            "transaction-detail-check-amount-label",
                            {},
                            "Amount"
                          )}
                        </span>
                      </Row.Item>
                      <Row.Item>
                        <div className={styles.checkDetailValue}>
                          {displayAmount ? `$${displayAmount}` : emptyValue}
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                  <div className={styles.checkDetailRowWrapper}>
                    <Row alignItems="center">
                      <Row.Item shrink>
                        <span className={styles.checkDetailLabel}>
                          {l10n.getString(
                            "transaction-detail-check-date-label",
                            {},
                            "Check date"
                          )}
                        </span>
                      </Row.Item>
                      <Row.Item>
                        <div className={styles.checkDetailValue}>
                          {displayCheckDate
                            ? DateTime.fromISO(displayCheckDate).toFormat(
                                "MM/dd/yyyy"
                              )
                            : emptyValue}
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                  <div className={styles.checkDetailRowWrapper}>
                    <Row alignItems="center">
                      <Row.Item shrink>
                        <span className={styles.checkDetailLabel}>
                          {l10n.getString(
                            "transaction-detail-check-memo-label",
                            {},
                            "Memo"
                          )}
                        </span>
                      </Row.Item>
                      <Row.Item>
                        <div className={styles.checkDetailValue}>
                          {displayMemo || emptyValue}
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                  <div className={styles.checkDetailRowWrapper}>
                    <Row alignItems="center">
                      <Row.Item shrink>
                        <span className={styles.checkDetailLabel}>
                          {l10n.getString(
                            "transaction-detail-check-number-label",
                            {},
                            "Check number"
                          )}
                        </span>
                      </Row.Item>
                      <Row.Item>
                        <div className={styles.checkDetailValue}>
                          {displayCheckNumber || emptyValue}
                        </div>
                      </Row.Item>
                    </Row>
                  </div>
                </div>
              )}
            </div>
          )}
        </React.Fragment>
      </CollapsibleCard>
    </div>
  );
};

const TransactionCheck = ({
  label,
  front,
  back,
  isCheck,
  payFrom,
  payTo,
  amount,
  memo,
  checkDate,
  checkNumber,
  checkDetailUuid,
  transactionId,
  onSaveSuccess,
  onDescriptionUpdate,
}: TransactionCheckProps) => {
  if (!(front || back)) {
    return null;
  }

  return (
    <CheckDetailProvider
      payFrom={payFrom}
      payTo={payTo}
      amount={amount}
      memo={memo}
      checkDate={checkDate}
      checkNumber={checkNumber}
      checkDetailUuid={checkDetailUuid}
      transactionId={transactionId}
      onSaveSuccess={onSaveSuccess}
      onDescriptionUpdate={onDescriptionUpdate}
    >
      <TransactionCheckContent
        label={label}
        front={front}
        back={back}
        isCheck={isCheck}
      />
    </CheckDetailProvider>
  );
};

export default TransactionCheck;
